import React, { useContext, useEffect, useRef } from 'react';
import styled from 'styled-components';
import {
  AccountStatus,
  GetSessionHydrationQuery,
  AccountModification,
  ImpersonationViewingMode,
  useGetSessionHydrationQuery,
} from '~/graphql/types';

import AccountPaused from '~/components/page/AccountStatus/AccountPaused';
import AccountCancelled from '~/components/page/AccountStatus/AccountCancelled';
import AccountModificationInProgress from '~/components/page/AccountStatus/AccountModification';
import ErrorScreen from '~/components/page/ErrorScreen';
import { WithNavigationFrame as NotFound } from '~/components/page/404';
import { assertNever } from '~/util/assertion';
import AccountLocked from '~/components/page/AccountStatus/AccountLocked';
import { Redirect, type RouteComponentProps } from '@gatsbyjs/reach-router';
import DHRouter from '~/components/atom/DHRouter';
import Tasks from '~/components/page/Tasks';
import Contacts from '~/components/page/Contacts';
import Automation from '~/components/page/Automation';
import Apps from '~/components/page/Apps';
import Settings from '~/components/page/Settings';
import External from '~/components/page/External';
import CreateActivities from '~/components/page/CreateActivities';
import useBeforeUnload from '~/hooks/useBeforeUnload';
import ToastsContainer from '~/components/organism/ToastsContainer';
import Dashboard from '~/components/page/Dashboard';
import SessionHydrationContext from '~/contexts/SessionHydrationContext';
import Accounts from '~/components/page/Accounts';
import { AuthContext } from '~/Root/Auth';
import ImpersonationContainer from '~/components/organism/ImpersonationContainer';
import { isNil } from 'ramda';
import NavigationFrame from '~/components/organism/NavigationFrame';
import useErrorReporter from '~/hooks/useErrorReporter';
import { useUnleashContext } from '@unleash/proxy-client-react';
import Wizard from '~/components/organism/Wizard';
import ViewingMode from '~/contexts/ViewingMode';
import DemoStrapiPages from '~/components/page/DemoStrapiPages';
import SetupSubscriptionPage from '~/components/page/Settings/Subscription/components/SetupSubscriptionPage';
import OnboardingWizardPage from '~/components/page/OnboardingWizardPage';
import isPopupWindow from '~/util/isPopupWindow';
import ScrollToTop from '~/components/atom/ScrollToTop';
import Loading from '~/components/atom/Loading';
import useDHFlag from '~/hooks/useDHFlag';
import Widget from '~/components/page/Widget';
import TooltipLayer from '~/components/TooltipLayer';
import Wrapper from '~/Wrapper';
import Forms from '~/components/page/Forms';
import DnDContext from '~/contexts/DnDContext';
import CommandPaletteProvider from '~/components/organism/CommandPaletteProvider';
import { setLocalStorageItem } from '~/util/localStorageKeys';
import { SELECTED_ACCOUNT_STORAGE_KEY } from '~/util/localStorageKeys';
import LFA from '~/components/page/LFA';
import useAssetsStateManager from '../../hooks/useAssetsStateManager';
import { trackEvent } from '~/components/organism/CookieBanner/utils/initializeAnalytics';
import useIntercom from '~/hooks/useIntercom';
import useSetupAnalytics from '../../hooks/useSetupAnalytics';

type Props = RouteComponentProps & {
  accountId: string;
};

const AuthRoutes: React.FCC<Props> = ({ accountId }) => {
  const updateUnleashContext = useUnleashContext();
  const intercom = useIntercom();

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const accountIdFromParams = searchParams.get('accountId');

    const shouldSwitchAccount =
      accountIdFromParams != null && accountIdFromParams !== accountId;

    if (shouldSwitchAccount) {
      searchParams.delete('accountId');
      const newUrl = `${window.location.pathname}${
        searchParams.toString() ? `?${searchParams.toString()}` : ''
      }`;

      setLocalStorageItem(SELECTED_ACCOUNT_STORAGE_KEY, accountIdFromParams);
      // force reload
      window.location.href = newUrl;
    }
  }, [accountId]);

  const { loading, error, data, refetch } = useGetSessionHydrationQuery({
    variables: { accountId },
  });
  useBeforeUnload('Let op: openstaande wijzigingen');
  useAssetsStateManager(data?.getSessionHydration.assets);

  useEffect(() => {
    if (!data?.getSessionHydration.me) return;
    if (data?.getSessionHydration.impersonatedBy != null) return;

    const isAccountAnLfaAccount = Boolean(data.getSessionHydration.lfaEnabled);

    intercom.update({
      // To add more attributes, see: https://www.intercom.com/help/en/articles/179-create-and-track-custom-data-attributes-cdas
      is_lfa_account: isAccountAnLfaAccount,
      current_account_name: data.getSessionHydration.account.name,
    });
  }, [data, intercom, data?.getSessionHydration?.lfaEnabled]);

  /** ----- ANALYTICS SETUP ----- */
  useSetupAnalytics({ sessionHydration: data?.getSessionHydration });
  /** ----- END OF ANALYTICS SETUP ----- */

  useEffect(() => {
    if (data && data.getSessionHydration.me) {
      void updateUnleashContext({ userId: data.getSessionHydration.me.id });

      const width = window.innerWidth || document.body.clientWidth;
      const height = window.innerHeight || document.body.clientHeight;

      const _width = Math.round(width / 100) * 100;
      const _height = Math.round(height / 100) * 100;

      const size = `${_width}x${_height}`;

      trackEvent({
        eventName: 'screenSize',
        parameters: {
          size,
          account_id: data.getSessionHydration.account.id,
          user_identifier: data.getSessionHydration.me.id,
        },
      });
    }
  }, [data, updateUnleashContext]);

  if (loading) {
    return (
      <LoadingContainer>
        <Loading />
      </LoadingContainer>
    );
  }

  if (!data && error) return <ErrorScreen />;
  if (!data) return <NotFound />;

  const { account } = data.getSessionHydration;

  return (
    <SessionHydrationContext.Provider
      value={{ data: data.getSessionHydration, refetch }}
    >
      <ViewingMode.Provider
        value={{
          viewingMode:
            data.getSessionHydration.impersonationOptions?.viewingMode ||
            ImpersonationViewingMode.Complete,
        }}
      >
        <Wrapper>
          <TooltipLayer />
          <AccountPageByStatus
            account={account}
            ongoingAccountModification={
              account.ongoingAccountModification ?? null
            }
          />
        </Wrapper>
        <ToastsContainer />
        {!isNil(data.getSessionHydration.impersonatedBy) && (
          <ImpersonationContainer />
        )}
      </ViewingMode.Provider>
    </SessionHydrationContext.Provider>
  );
};

/**
 * Returns the appropriate account page for the account status.
 *
 * @param accountStatus AccountStatus
 */
const AccountPageByStatus = ({
  account,
  ongoingAccountModification,
}: {
  account: GetSessionHydrationQuery['getSessionHydration']['account'];
  ongoingAccountModification: AccountModification | null;
}): JSX.Element => {
  const contentContainerRef = useRef<HTMLDivElement | null>(null);
  const { isDHAdmin } = useContext(AuthContext);
  const isDeveloper = useDHFlag('is-developer');
  const status = account.status ?? AccountStatus.Active;

  // This initialises the error reporter, ensuring contexts get set when they are available.
  useErrorReporter();

  switch (status) {
    case AccountStatus.Incomplete:
      return (
        <DHRouter>
          <SetupSubscriptionPage path="/" />
        </DHRouter>
      );

    case AccountStatus.Locked:
      return (
        <DHRouter>
          <AccountLocked path="/locked" />
        </DHRouter>
      );

    case AccountStatus.Paused:
      return (
        <DHRouter>
          <AccountPaused path="/paused" />
        </DHRouter>
      );

    case AccountStatus.Cancelled:
    case AccountStatus.Deleted:
      return (
        <DHRouter>
          <AccountCancelled path="/cancelled" />
        </DHRouter>
      );

    case AccountStatus.Active: {
      if (
        !isNil(ongoingAccountModification) &&
        ongoingAccountModification === AccountModification.UpdatePlan
      ) {
        return <AccountModificationInProgress default />;
      }

      const isOnboardingCompleted =
        account.AccountSettings?.metadata?.setup?.completed === true;
      const isInPopupWindow = isPopupWindow();

      return (
        <>
          <Wizard header="Global wizard" id="onboarding-wizard" steps={[]}>
            {!isOnboardingCompleted && !isInPopupWindow ? (
              <DHRouter>
                <OnboardingWizardPage default path="/" />
              </DHRouter>
            ) : (
              <DnDContext>
                <NavigationFrame ref={contentContainerRef}>
                  <DHRouter>
                    <Dashboard path="/" />
                    <Tasks path="/tasks/*" />
                    <Contacts path="/contacts/*" />
                    <Automation path="/automation/*" />
                    <Apps path="/apps/*" />
                    <Widget path="/widget/*" />
                    <Forms path="/forms/*" />
                    <Settings path="/settings/*" />
                    <External path="/external/*" />
                    <LFA path="/lfa/*" />
                    <CreateActivities path="/createActivities" />
                    <DemoStrapiPages path="/strapi/:slug" />

                    {isDHAdmin && <Accounts path="/accounts/*" />}
                    {(isDHAdmin || isDeveloper) && (
                      <Redirect
                        from="/experimental/widget-url-builder"
                        to="/widget/link-builder"
                        noThrow
                      />
                    )}
                    <NotFound default />
                  </DHRouter>
                  <ScrollToTop containerRef={contentContainerRef} />
                  <CommandPaletteProvider />
                </NavigationFrame>
              </DnDContext>
            )}
          </Wizard>
        </>
      );
    }
    default:
      return assertNever(status);
  }
};

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

export default AuthRoutes;
